/**
 * VerifyEmailPage — Vérification d'email via token reçu par mail.
 *
 * Refonte R5 (mai 2026) :
 *   - LogoMark adapté à chaque état (success/loading/error)
 *   - Card glass + Alert avec icône
 *   - Spinner moderne sur état loading
 *
 * Logique métier 100% préservée :
 *   - 5 statuts (loading, success, invalid, expired, error)
 *   - useRef pour éviter double-exec (React StrictMode)
 *   - Form de renvoi du mail sur échecs avec 429 handling
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, Input, Field, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { PublicLayout } from '@/components/PublicLayout';
import { useAuth } from '@/lib/auth-context';
import { ApiException } from '@/lib/api';
import { CheckCircle2, AlertCircle, Mail, Loader2 } from 'lucide-react';

type Status = 'loading' | 'success' | 'invalid' | 'expired' | 'error';

export function VerifyEmailPage() {
  const { verifyEmail, resendVerification } = useAuth();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') ?? '', [params]);

  const [status, setStatus] = useState<Status>('loading');
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSubmitted, setResendSubmitted] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // useRef : empêche la double exécution sous React StrictMode (dev mode).
  // En prod le StrictMode est désactivé, mais ce verrou est utile pour gérer
  // tout re-render qui re-monterait le composant (ex: navigation rapide back/forward).
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    if (!token) {
      setStatus('invalid');
      return;
    }
    (async () => {
      try {
        const result = await verifyEmail(token);
        setVerifiedEmail(result.email);
        setStatus('success');
      } catch (err) {
        if (err instanceof ApiException) {
          if (err.payload.code === 'EXPIRED_VERIFY_TOKEN') setStatus('expired');
          else if (err.payload.code === 'INVALID_VERIFY_TOKEN') setStatus('invalid');
          else setStatus('error');
        } else {
          setStatus('error');
        }
      }
    })();
  }, [token, verifyEmail]);

  async function handleResend(e: React.FormEvent) {
    e.preventDefault();
    setResendError(null);
    setResendLoading(true);
    try {
      await resendVerification(resendEmail);
      setResendSubmitted(true);
    } catch (err) {
      if (err instanceof ApiException && err.status === 429) {
        setResendError('Trop de tentatives. Veuillez patienter quelques minutes.');
      } else {
        setResendError('Une erreur est survenue. Réessayez dans un instant.');
      }
    } finally {
      setResendLoading(false);
    }
  }

  // ---------- SUCCESS ----------
  if (status === 'success') {
    return (
      <PublicLayout maxWidth="sm">
        <div className="text-center space-y-5">
          <div className="flex justify-center">
            <div
              className="inline-grid h-14 w-14 place-items-center rounded-r-xl bg-gradient-success text-white shadow-glow-success"
              style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-success) / 0.55), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
              aria-hidden="true"
            >
              <CheckCircle2 className="h-7 w-7" />
            </div>
          </div>
          <div>
            <SectionEyebrow as="div">Vérification réussie</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
              Email vérifié&nbsp;!
            </h1>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed">
            Votre adresse <strong className="text-ink">{verifiedEmail}</strong> est confirmée.
            Vous pouvez désormais vous connecter à votre compte.
          </p>
          <div className="pt-2">
            <Link to="/connexion">
              <Button size="lg">Me connecter</Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // ---------- LOADING ----------
  if (status === 'loading') {
    return (
      <PublicLayout maxWidth="sm">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <LogoMark icon={Loader2} accent="tech" size="lg" aria-label="Vérification en cours" className="animate-spin" />
          </div>
          <p className="text-sm text-ink-muted">Vérification de votre email en cours…</p>
        </div>
      </PublicLayout>
    );
  }

  // ---------- INVALID / EXPIRED / ERROR ----------
  // Tous les cas d'échec proposent de renvoyer un mail
  const headline =
    status === 'expired' ? 'Ce lien a expiré'
    : status === 'invalid' ? 'Lien invalide'
    : 'Une erreur est survenue';

  const detail =
    status === 'expired'
      ? 'Les liens de vérification sont valables 24 heures. Demandez un nouveau lien ci-dessous.'
      : status === 'invalid'
      ? 'Ce lien est invalide ou a déjà été utilisé. Demandez un nouveau lien ci-dessous.'
      : 'Nous n\'avons pas pu vérifier votre email. Réessayez ou demandez un nouveau lien.';

  if (resendSubmitted) {
    return (
      <PublicLayout maxWidth="sm">
        <div className="text-center space-y-5">
          <div className="flex justify-center">
            <LogoMark icon={Mail} accent="tech" size="lg" aria-label="Email envoyé" />
          </div>
          <div>
            <SectionEyebrow as="div">Demande envoyée</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
              Demande envoyée
            </h1>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed">
            Si un compte existe avec l'adresse <strong className="text-ink">{resendEmail}</strong>{' '}
            et qu'il n'est pas encore vérifié, un nouveau lien vous sera envoyé.
          </p>
          <p className="text-xs text-ink-subtle leading-relaxed">
            Pensez à vérifier vos courriers indésirables.
          </p>
          <div className="pt-2">
            <Link to="/connexion">
              <Button variant="secondary">Retour à la connexion</Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout maxWidth="sm">
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex justify-center">
            <div
              className="inline-grid h-12 w-12 place-items-center rounded-r-xl bg-gradient-to-b from-urgent to-urgent/70 text-white shadow-glow-urgent"
              style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-urgent) / 0.55), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
              aria-hidden="true"
            >
              <AlertCircle className="h-6 w-6" />
            </div>
          </div>
          <SectionEyebrow as="div" className="mt-4">Vérification email</SectionEyebrow>
          <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
            {headline}
          </h1>
          <p className="mt-2 text-sm text-ink-muted">{detail}</p>
        </div>

        <Card variant="glass" padded>
          <form onSubmit={handleResend} className="space-y-4" noValidate>
            {resendError && (
              <Alert variant="error" icon={AlertCircle}>
                {resendError}
              </Alert>
            )}

            <Field label="Votre adresse email" htmlFor="resendEmail" required>
              <Input
                id="resendEmail"
                type="email"
                autoComplete="email"
                placeholder="vous@exemple.fr"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                required
                className="h-12 sm:h-11"
              />
            </Field>

            <Button type="submit" loading={resendLoading} className="w-full" size="lg">
              Envoyer un nouveau lien
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-ink-muted">
          <Link
            to="/connexion"
            className="font-medium text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md px-1"
          >
            Retour à la connexion
          </Link>
        </p>
      </div>
    </PublicLayout>
  );
}
